<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="md:flex md:items-center md:justify-between" data-testid="api-keys-header">
      <div class="min-w-0 flex-1">
        <h2
          class="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:truncate sm:text-3xl sm:tracking-tight"
        >
          API Keys
        </h2>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Manage your API keys to integrate with our services.
        </p>
      </div>
      <div class="mt-4 flex gap-3 md:mt-0 md:ml-4">
        <Button variant="outline" @click="() => refresh()" :loading="isLoading">
          <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refresh
        </Button>
        <Button @click="openCreateModal" data-testid="create-key-button">
          <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          Create API Key
        </Button>
      </div>
    </div>

    <!-- Navigation Tabs -->
    <div class="border-b border-gray-200 dark:border-gray-600">
      <nav class="-mb-px flex space-x-8" aria-label="Tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          :class="[
            activeTab === tab.id
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500',
            'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center',
          ]"
        >
          <component :is="tab.icon" class="h-5 w-5 mr-2" />
          {{ tab.name }}
        </button>
      </nav>
    </div>
    <!-- Tab Content -->
    <div class="mt-6">
      <!-- API Keys Management Tab -->
      <div v-show="activeTab === 'keys'" class="space-y-6">
        <Alert v-if="error" variant="error" data-testid="error-message">
          {{ error }}
        </Alert>
        <!-- Statistics -->
        <div
          v-if="apiKeyStats.total > 0"
          class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
          data-testid="api-keys-stats"
        >
          <Card class="p-6 hover:shadow-lg transition-shadow duration-200">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div
                  class="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center"
                >
                  <svg
                    class="w-6 h-6 text-blue-600 dark:text-blue-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v-2H7v-2H4a1 1 0 01-1-1v-4c0-2.632 1.357-5.368 3.857-6.858A8.978 8.978 0 0112 3a9 9 0 019 9v0a9.003 9.003 0 01-4 7.429z"
                    />
                  </svg>
                </div>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Total Keys</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white">
                  {{ apiKeyStats.total }}
                </p>
              </div>
            </div>
          </Card>

          <Card class="p-6 hover:shadow-lg transition-shadow duration-200">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div
                  class="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center"
                >
                  <svg
                    class="w-6 h-6 text-green-600 dark:text-green-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Active</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white">
                  {{ apiKeyStats.active }}
                </p>
              </div>
            </div>
          </Card>

          <Card class="p-6 hover:shadow-lg transition-shadow duration-200">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div
                  class="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl flex items-center justify-center"
                >
                  <svg
                    class="w-6 h-6 text-yellow-600 dark:text-yellow-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Expiring</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white">
                  {{ apiKeyStats.expiring }}
                </p>
              </div>
            </div>
          </Card>
        </div>

        <!-- Filters and Search -->
        <Card class="p-4">
          <div
            class="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4"
          >
            <div class="flex-1 max-w-md">
              <Input
                v-model="searchTerm"
                type="text"
                placeholder="Search API keys..."
                class="w-full"
                data-testid="search-input"
              >
                <template #prefix>
                  <svg
                    class="h-4 w-4 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </template>
              </Input>
            </div>

            <div class="flex items-center space-x-4">
              <select
                v-model="selectedStatus"
                class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                data-testid="status-filter"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="expiring_soon">Expiring Soon</option>
              </select>

              <select
                v-model="sortBy"
                class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              >
                <option value="date">Sort by Date</option>
                <option value="name">Sort by Name</option>
              </select>
            </div>
          </div>
        </Card>

        <!-- Loading State -->
        <div v-if="isLoading && !hasApiKeys" class="flex justify-center py-12" data-testid="loading-spinner">
          <LoadingSpinner size="lg" />
        </div>

        <div data-testid="api-keys-list">
          <div v-if="filteredApiKeys.length > 0" class="space-y-4">
            <ApiKeyCard
              v-for="apiKey in filteredApiKeys"
              :key="apiKey.id"
              :api-key="apiKey"
              :show-usage="false"
              @delete="handleDelete"
              @view-usage="handleViewUsage"
              @regenerate="handleRegenerate"
              @edit-permissions="handleEditPermissions"
            />
          </div>

          <Card v-else class="p-12" data-testid="empty-state">
            <div class="text-center">
              <svg
                class="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v-2H7v-2H4a1 1 0 01-1-1v-4c0-2.632 1.357-5.368 3.857-6.858A8.978 8.978 0 0112 3a9 9 0 019 9v0a9.003 9.003 0 01-4 7.429z"
                />
              </svg>
              <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                {{ searchTerm || selectedStatus !== 'all' ? 'No matching API keys' : 'No API keys' }}
              </h3>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {{
                  searchTerm || selectedStatus !== 'all'
                    ? 'Try adjusting your search criteria or filters.'
                    : 'Get started by creating your first API key.'
                }}
              </p>
              <div v-if="!searchTerm && selectedStatus === 'all'" class="mt-6">
                <Button @click="openCreateModal">
                  <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Create Your First API Key
                </Button>
              </div>
            </div>
          </Card>
        </div>

        <!-- Pagination -->
        <div
          v-if="totalPages > 1"
          class="flex items-center justify-between border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6"
          data-testid="pagination"
        >
          <div class="flex flex-1 justify-between sm:hidden">
            <Button variant="outline" @click="previousPage" :disabled="currentPage === 1">
              Previous
            </Button>
            <Button variant="outline" @click="nextPage" :disabled="currentPage === totalPages">
              Next
            </Button>
          </div>
          <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p class="text-sm text-gray-700 dark:text-gray-300">
                Showing
                {{ ' ' }}
                <span class="font-medium">{{ (currentPage - 1) * perPage + 1 }}</span>
                {{ ' ' }}
                to
                {{ ' ' }}
                <span class="font-medium">{{ Math.min(currentPage * perPage, total) }}</span>
                {{ ' ' }}
                of
                {{ ' ' }}
                <span class="font-medium">{{ total }}</span>
                {{ ' ' }}
                results
              </p>
            </div>
            <div>
              <nav
                class="isolate inline-flex -space-x-px rounded-md shadow-sm"
                aria-label="Pagination"
              >
                <Button
                  variant="outline"
                  @click="previousPage"
                  :disabled="currentPage === 1"
                  class="rounded-r-none"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  @click="nextPage"
                  :disabled="currentPage === totalPages"
                  class="rounded-l-none"
                  data-testid="pagination-next"
                >
                  Next
                </Button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <!-- Usage Analytics Tab -->
      <div v-show="activeTab === 'analytics'" class="space-y-6" data-testid="usage-analytics">
        <Card class="p-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300">API Key</h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                Select an API key to view usage analytics
              </p>
            </div>
            <div class="min-w-[220px]">
              <select
                v-model="selectedKeyForAnalytics"
                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              >
                <option value="" disabled>Select API key</option>
                <option v-for="apiKey in apiKeys" :key="apiKey.id" :value="apiKey.id">
                  {{ apiKey.name }}
                </option>
              </select>
            </div>
          </div>
        </Card>
        <ApiKeyUsageChart :key-id="selectedKeyForAnalytics || ''" data-testid="response-time-chart" />
      </div>

      <!-- Expiration Management Tab -->
      <div v-show="activeTab === 'expiration'" class="space-y-6">
        <ApiKeyExpirationManager @key-extended="handleKeyExtended" />
      </div>

      <!-- Permissions Tab -->
      <div v-show="activeTab === 'permissions'" class="space-y-6">
        <!-- Key Selector -->
        <Card class="p-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300">API Key</h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                Select an API key to view or manage its permissions
              </p>
            </div>
            <div class="min-w-[220px]">
              <select
                v-model="selectedKeyForPermissions"
                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              >
                <option value="" disabled>Select API key</option>
                <option
                  v-for="apiKey in fineGrainedKeys"
                  :key="apiKey.id"
                  :value="apiKey.id"
                >
                  {{ apiKey.name }}
                </option>
              </select>
            </div>
          </div>
        </Card>

        <div v-if="!selectedKeyForPermissions" class="text-center py-8">
          <p class="text-sm text-gray-500 dark:text-gray-400">
            Select an API key above to view or manage its permissions.
            Scope-based keys show their predefined permission set. Fine-grained keys can be edited.
          </p>
        </div>

        <ApiKeyPermissionsManager
          v-else
          :api-key="selectedApiKeyForPermissions"
          @permissions-updated="handlePermissionsUpdated"
        />
      </div>
    </div>

    <!-- Modals -->
    <CreateApiKeyModal v-model:open="createModalOpen" @created="handleApiKeyCreated" />

    <RegenerateApiKeyModal
      v-model:open="regenerateModalOpen"
      :api-key="selectedApiKeyForRegenerate"
      @regenerated="handleRegenerateCompleted"
    />

    <ConfirmDeleteApiKeyModal
      v-model:open="deleteModalOpen"
      :api-key="selectedApiKeyForDeletion"
      @deleted="handleApiKeyDeleted"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, h, watch } from 'vue';
  import { Alert, Button, LoadingSpinner, Card, Input, useNotification } from '@asabdev/asab-ui';
  import {
    ApiKeyCard,
    CreateApiKeyModal,
    RegenerateApiKeyModal,
    ConfirmDeleteApiKeyModal,
    ApiKeyUsageChart,
    ApiKeyExpirationManager,
    ApiKeyPermissionsManager,
  } from '@/features/api-keys/components';
  import { useApiKeys } from '@/features/api-keys/composables';
  import type { ApiKey } from '@/features/api-keys/types';

  // Tab configuration
  const tabs = [
    {
      id: 'keys',
      name: 'API Keys',
      icon: () =>
        h(
          'svg',
          {
            class: 'h-5 w-5',
            fill: 'none',
            viewBox: '0 0 24 24',
            stroke: 'currentColor',
          },
          [
            h('path', {
              'stroke-linecap': 'round',
              'stroke-linejoin': 'round',
              'stroke-width': '2',
              d: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v-2H7v-2H4a1 1 0 01-1-1v-4c0-2.632 1.357-5.368 3.857-6.858A8.978 8.978 0 0112 3a9 9 0 019 9v0a9.003 9.003 0 01-4 7.429z',
            }),
          ]
        ),
    },
    {
      id: 'analytics',
      name: 'Usage Analytics',
      icon: () =>
        h(
          'svg',
          {
            class: 'h-5 w-5',
            fill: 'none',
            viewBox: '0 0 24 24',
            stroke: 'currentColor',
          },
          [
            h('path', {
              'stroke-linecap': 'round',
              'stroke-linejoin': 'round',
              'stroke-width': '2',
              d: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
            }),
          ]
        ),
    },
    {
      id: 'expiration',
      name: 'Expiration',
      icon: () =>
        h(
          'svg',
          {
            class: 'h-5 w-5',
            fill: 'none',
            viewBox: '0 0 24 24',
            stroke: 'currentColor',
          },
          [
            h('path', {
              'stroke-linecap': 'round',
              'stroke-linejoin': 'round',
              'stroke-width': '2',
              d: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
            }),
          ]
        ),
    },
    {
      id: 'permissions',
      name: 'Permissions',
      icon: () =>
        h(
          'svg',
          {
            class: 'h-5 w-5',
            fill: 'none',
            viewBox: '0 0 24 24',
            stroke: 'currentColor',
          },
          [
            h('path', {
              'stroke-linecap': 'round',
              'stroke-linejoin': 'round',
              'stroke-width': '2',
              d: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
            }),
          ]
        ),
    },
  ];

  const {
    // State
    apiKeys,
    filteredApiKeys,
    apiKeyStats,
    isLoading,
    error,
    hasApiKeys,
    total,
    currentPage,
    perPage,
    totalPages,

    // Filters
    searchTerm,
    selectedStatus,
    sortBy,

    // Actions
    refresh,
    initialize,

    // Pagination
    setPage,
  } = useApiKeys();

  const { showSuccess } = useNotification();

  // Local state
  const activeTab = ref('keys');
  const createModalOpen = ref(false);
  const regenerateModalOpen = ref(false);
  const deleteModalOpen = ref(false);
  const selectedApiKeyForDeletion = ref<ApiKey | null>(null);
  const selectedApiKeyForRegenerate = ref<ApiKey | null>(null);
  const selectedKeyForAnalytics = ref<string | null>(null);
  const selectedKeyForPermissions = ref<string>('');

  // All keys shown in permissions tab — scope keys show read-only notice, fine-grained are editable
  const fineGrainedKeys = computed(() => apiKeys.value || []);

  const selectedApiKeyForPermissions = computed(() =>
    (apiKeys.value || []).find((k: ApiKey) => k.id === selectedKeyForPermissions.value) || null
  );

  // Methods
  const openCreateModal = () => {
    createModalOpen.value = true;
  };

  const handleDelete = (apiKey: ApiKey) => {
    selectedApiKeyForDeletion.value = apiKey;
    deleteModalOpen.value = true;
  };

  const handleViewUsage = (apiKey: ApiKey) => {
    selectedKeyForAnalytics.value = apiKey.id;
    activeTab.value = 'analytics';
    showSuccess(`Viewing usage statistics for "${apiKey.name}"`);
  };

  const handleEditPermissions = (apiKey: ApiKey) => {
    selectedKeyForPermissions.value = apiKey.id;
    activeTab.value = 'permissions';
  };

  const handleRegenerate = (apiKey: ApiKey) => {
    selectedApiKeyForRegenerate.value = apiKey;
    regenerateModalOpen.value = true;
  };

  const handleRegenerateCompleted = async () => {
    await refresh();
  };

  const handleApiKeyCreated = () => {
    // Notification is already handled by the composable
    refresh();
  };

  const handleApiKeyDeleted = (apiKey: ApiKey) => {
    showSuccess(`API key "${apiKey.name}" deleted successfully!`);
    selectedApiKeyForDeletion.value = null;
    // Refresh the list to ensure UI is in sync
    refresh();
  };

  // Phase 5.4 event handlers
  const handleKeyExtended = (apiKey: ApiKey) => {
    showSuccess(`Expiration extended for "${apiKey.name}"`);
    refresh();
  };

  const handlePermissionsUpdated = () => {
    showSuccess(`Permissions updated successfully`);
    refresh();
  };

  const previousPage = () => {
    if (currentPage.value > 1) {
      setPage(currentPage.value - 1);
    }
  };

  const nextPage = () => {
    if (currentPage.value < totalPages.value) {
      setPage(currentPage.value + 1);
    }
  };

  watch(
    () => activeTab.value,
    tab => {
      if (tab !== 'analytics') return;
      if (selectedKeyForAnalytics.value) return;
      if (!apiKeys.value || apiKeys.value.length === 0) return;
      selectedKeyForAnalytics.value = apiKeys.value[0]?.id ?? null;
    }
  );

  // Initialize data on mount
  onMounted(() => {
    initialize();
  });
</script>
